import type { CSSProperties } from "react";
import type { Asset, UserRole, View } from "../../types";
import { daysUntil, formatDate, nearestService } from "../../utils/assetDates";
import "./dashboard.css";

type Props = {
  assets: Asset[];
  name: string;
  role: UserRole;
  navigate: (view: View) => void;
};

export default function Dashboard({ assets, name, role, navigate }: Props) {
  const raft = assets.filter((asset) => asset.type === "Life Raft");
  const boat = assets.filter((asset) => asset.type === "Life Boat");
  const operational = assets.filter(
    (asset) => asset.status === "Operational",
  ).length;
  const attention = assets.length - operational;
  const readiness = assets.length
    ? Math.round((operational / assets.length) * 100)
    : 0;

  const actions = assets
    .map((asset) => ({ asset, service: nearestService(asset) }))
    .filter(({ asset, service }) => {
      if (asset.status !== "Operational") return true;
      return service ? daysUntil(service.date) <= 14 : false;
    })
    .sort((left, right) => {
      if (!left.service) return 1;
      if (!right.service) return -1;
      return left.service.date.localeCompare(right.service.date);
    })
    .slice(0, 5);

  const today = new Date();
  const day = String(today.getDate()).padStart(2, "0");
  const monthYear = new Intl.DateTimeFormat("en-MY", {
    month: "long",
    year: "numeric",
  })
    .format(today)
    .toUpperCase();
  const weekday = new Intl.DateTimeFormat("en-MY", {
    weekday: "long",
  }).format(today);

  return (
    <div className="dashboard-module">
      <section className="welcome">
        <div>
          <span className="status-pill">
            <i /> Database connected
          </span>
          <h2>Welcome, {name}.</h2>
          <p>
            {role === "Admin"
              ? "Monitor Seascape Synergy safety assets and user activity from one workspace."
              : "Monitor marine safety assets and keep service information current."}
          </p>
        </div>
        <div className="date-card">
          <span>{day}</span>
          <div>
            <strong>{monthYear}</strong>
            <small>{weekday} · Kuala Lumpur</small>
          </div>
        </div>
      </section>

      <section className="stats-grid">
        <article className="stat-card cyan">
          <div className="stat-icon">◫</div>
          <div>
            <p>LIFE RAFTS</p>
            <h3>{raft.length}</h3>
            <span>
              {raft.filter((asset) => asset.status === "Operational").length}{" "}
              operational
            </span>
          </div>
          <button type="button" onClick={() => navigate("Life Raft")}>
            →
          </button>
        </article>

        <article className="stat-card blue">
          <div className="stat-icon">◇</div>
          <div>
            <p>LIFE BOATS</p>
            <h3>{boat.length}</h3>
            <span>
              {boat.filter((asset) => asset.status === "Operational").length}{" "}
              operational
            </span>
          </div>
          <button type="button" onClick={() => navigate("Life Boat")}>
            →
          </button>
        </article>

        <article className="stat-card green">
          <div className="stat-icon">✓</div>
          <div>
            <p>READINESS</p>
            <h3>{readiness}%</h3>
            <span>{operational} serviceable</span>
          </div>
        </article>

        <article className="stat-card amber">
          <div className="stat-icon">!</div>
          <div>
            <p>NEEDS ATTENTION</p>
            <h3>{attention}</h3>
            <span>Action required</span>
          </div>
        </article>
      </section>

      <section className="dashboard-grid">
        <article className="panel attention-panel">
          <div className="panel-head">
            <div>
              <p>UPCOMING ACTIONS</p>
              <h3>Service &amp; maintenance</h3>
            </div>
          </div>
          {actions.length ? (
            actions.map(({ asset, service }) => (
              <button
                className="action-row"
                type="button"
                key={asset.id}
                onClick={() => navigate(asset.type)}
              >
                <span
                  className={`action-icon ${asset.status === "Expired" ? "red" : "amber"}`}
                >
                  !
                </span>
                <span>
                  <strong>{asset.name}</strong>
                  <small>
                    {asset.clientName} · {asset.assetId} · {asset.vessel}
                  </small>
                </span>
                <span className="action-date">
                  <strong>{formatDate(service?.date)}</strong>
                  <small>{service?.label ?? asset.status}</small>
                </span>
              </button>
            ))
          ) : (
            <div className="dashboard-empty">
              No assets currently need attention.
            </div>
          )}
        </article>

        <article className="panel compliance">
          <div className="panel-head">
            <div>
              <p>ASSET HEALTH</p>
              <h3>Overall readiness</h3>
            </div>
            <span className="mini-live">
              <i /> Live
            </span>
          </div>
          <div
            className="donut"
            style={{ "--percent": `${readiness}%` } as CSSProperties}
          >
            <div>
              <strong>{readiness}%</strong>
              <span>Serviceable</span>
            </div>
          </div>
          <div className="legend">
            <span>
              <i className="good" /> Operational <b>{operational}</b>
            </span>
            <span>
              <i className="warn" /> Attention <b>{attention}</b>
            </span>
          </div>
        </article>
      </section>
    </div>
  );
}
